import { Fragment, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Grid,
  Heading,
  Tabs,
  Separator,
  Spinner,
} from '@design-systems/canvas-ui'
import SearchResultsGroup from '../components/searchResults/SearchResultsGroup'
import styles from './SearchResultsPage.module.css'
import { PageLayout } from '@plm-core/utils'
import RecentSearchesSideBar from '../components/searchResults/RecentSearchesSideBar'
import { FEDERATED_GET_GLOBAL_SEARCHES } from '../../../graphql/queries/globalSearch/globalSearchQueries'
import { useQuery } from '@apollo/client/react'
import { GROUP_CONFIGS } from '../../../config/globalSearchConsts'
import { getRefineSearchLink } from '../utils/globalSearchHelpers'

const SearchResults = () => {
  const [hoverIndex, setHoverIndex] = useState<number>(-1)
  const [activeTab, setActiveTab] = useState('All')
  const { searchTerm } = useParams<{ searchTerm?: string }>()
  const searchText = searchTerm ?? ''

  const { data: globalSearchTabs, loading: tabsLoading } = useQuery(
    FEDERATED_GET_GLOBAL_SEARCHES,
    {
      variables: {
        domain: 'ALL',
        page: 0,
        searchText,
        size: 1,
      },
      skip: !searchText,
    },
  )

  const totalsByDomain = new Map(
    globalSearchTabs?.globalSearch?.results?.map((r) => [
      r.domain,
      r.pageInfo?.totalElements ?? 0,
    ]) ?? [],
  )

  const populatedGroups = tabsLoading
    ? GROUP_CONFIGS
    : GROUP_CONFIGS.filter(
        (group) => (totalsByDomain.get(group.domain) ?? 0) > 0,
      )

  const allTabs = [
    { id: 'All', name: 'All' },
    ...populatedGroups.map((group) => ({ id: group.domain, name: group.name })),
  ]

  const visibleGroups = populatedGroups.filter(
    (group) => activeTab === 'All' || activeTab === group.name,
  )

  return (
    <Grid.Container>
      <RecentSearchesSideBar
        setHoverIndex={setHoverIndex}
        hoverIndex={hoverIndex}
      />
      <Separator direction="vertical" />
      <Grid.Item
        size={{ xs: true }}
        className={`hc8-pv-4x hc8-ph-8x ${styles.scrollableContent}`}
      >
        <PageLayout withPageHeader>
          <Grid.Item className="hc8-pv-2x" size={{ xs: 12 }}>
            <Heading>Search results for '{searchTerm}'</Heading>
          </Grid.Item>
          {!visibleGroups.length && !tabsLoading ? null : (
            <Grid.Container size={{ xs: 12 }} className="hc8-pt-1x hc8-pb-4x">
              <Grid.Item size={{ xs: 12 }}>
                <Tabs
                  activeTab={activeTab}
                  onTabSelect={(name: string) => setActiveTab(name)}
                >
                  {allTabs.map((tab) => (
                    <Tabs.Item
                      data-testid={`tab-${tab.name}`}
                      key={tab.id}
                      name={tab.name}
                    >
                      {tab.name}
                    </Tabs.Item>
                  ))}
                </Tabs>
              </Grid.Item>
            </Grid.Container>
          )}
          {!visibleGroups.length && !tabsLoading && (
            <Grid.Container justify="center" className="hc8-pt-8x">
              <Grid.Item>
                <Heading>No results found</Heading>
              </Grid.Item>
            </Grid.Container>
          )}
          {tabsLoading ? (
            <Grid.Container
              size={{ xs: 12 }}
              justify="center"
              className="hc8-pt-8x"
            >
              <Grid.Item>
                <Spinner />
              </Grid.Item>
            </Grid.Container>
          ) : (
            visibleGroups.map((group, index) => {
              const isLast = index === visibleGroups.length - 1
              return (
                <Fragment key={group.domain}>
                  <Grid.Item
                    className={index > 0 ? 'hc8-pt-3x' : undefined}
                    size={{ xs: 12 }}
                  >
                    <SearchResultsGroup
                      name={group.name}
                      domain={group.domain}
                      searchText={searchText}
                      refineSearchLink={getRefineSearchLink(
                        group.domain,
                        searchText,
                      )}
                      icon={group.icon}
                      iconColor={group.iconColor}
                    />
                  </Grid.Item>
                  {!isLast && (
                    <Grid.Item size={{ xs: 12 }}>
                      <Separator />
                    </Grid.Item>
                  )}
                </Fragment>
              )
            })
          )}
        </PageLayout>
      </Grid.Item>
    </Grid.Container>
  )
}

export default SearchResults
